import React, { useLayoutEffect, useRef } from 'react';
import { fmtNumber } from '../../format';

// Pieces shared by the Time & Attendance screens (Stephanet's MissedDaysStatement exports and attendance helpers).

export const Chip = ({ tone, children }) => (children ? <span className={`mdr-chip t-${tone || 'gray'}`}>{children}</span> : <span className="mds-dash">—</span>);

export const Kpi = ({ label, value, Ico, tone, note, i = 0 }) => (
  <div className={`md-kpi lc-${tone}`} style={{ animationDelay: `${i * 35}ms` }}>
    <div className="md-kpi-head"><span>{label}</span>{Ico && <Ico size={17} />}</div>
    <b>{typeof value === 'number' ? fmtNumber(value) : value}</b>
    {note && <small>{note}</small>}
  </div>
);

export const iso = d => {
  const x = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return x.toISOString().slice(0, 10);
};
export const shiftDay = (s, n) => { const d = new Date(`${s}T00:00:00`); d.setDate(d.getDate() + n); return iso(d); };
export const shiftMonth = (m, n) => { const d = new Date(`${m}-01T00:00:00`); d.setMonth(d.getMonth() + n); return iso(d).slice(0, 7); };
export const monthLabel = m => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
export const hhmm = h => {
  if (!h) return '—';
  const m = Math.round(Number(h) * 60);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};
export const fmtDay = s => new Date(`${s}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
export const stamp = s => (s ? new Date(s).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// the first `count` columns stay in place while the table scrolls sideways
export function useFrozen(deps, count = 3) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const t = ref.current;
    if (!t || typeof ResizeObserver === 'undefined') return undefined;
    const set = () => {
      let left = 0;
      [...t.querySelectorAll('thead th')].slice(0, count).forEach((th, i) => { t.style.setProperty(`--fz${i + 1}`, `${left}px`); left += th.getBoundingClientRect().width; });
    };
    set();
    const ro = new ResizeObserver(set);
    t.querySelectorAll('thead th').forEach(th => ro.observe(th));
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}

const esc = v => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
export { esc };

// an Excel-readable HTML table download (Stephanet's workhistory/shared downloadXls)
export function downloadXls(filename, title, body) {
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"/>
    <style>th{background:#0b318b;color:#fff;font-weight:bold} td,th{padding:4px 8px;font-family:Calibri,Arial;font-size:11pt} h2,h3{font-family:Calibri,Arial}</style>
    </head><body><h2>${esc(title)}</h2>${body}</body></html>`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob(['﻿', html], { type: 'application/vnd.ms-excel' }));
  a.download = filename;
  a.click();
}

// prints one record through a hidden frame, leaving the page as it is
export function printDoc(title, rows, footer) {
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(frame);
  const body = rows.filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('');
  frame.contentDocument.write(`<html><head><title>${esc(title)}</title><style>
    body{font-family:Inter,Arial,sans-serif;color:#0d1b24;margin:32px} h1{font-size:18px;margin:0 0 4px;color:#0b318b} p{margin:0 0 18px;color:#51626c;font-size:12px}
    table{border-collapse:collapse;width:100%;font-size:13px} th{width:34%;text-align:left;background:#f1f5fb;color:#334155} th,td{border:1px solid #dbe3f0;padding:8px 10px;vertical-align:top}
    footer{margin-top:28px;font-size:11px;color:#64748b;border-top:1px solid #dbe3f0;padding-top:8px}</style></head>
    <body><h1>${esc(title)}</h1><p>${esc(footer || '')}</p><table>${body}</table><footer>Confidential — Rifitel HR · Printed ${new Date().toLocaleString('en-GB')}</footer></body></html>`);
  frame.contentDocument.close();
  setTimeout(() => { frame.contentWindow.focus(); frame.contentWindow.print(); setTimeout(() => frame.remove(), 1000); }, 200);
}
